// Iter-207r — Sidebar drag-and-drop edit mode hook.
//
// Strategy : non-destructive DOM reordering. The AppLayout JSX is left as-is
// (huge tree, deep RBAC logic). The hook reads the current DOM order under
// <nav data-testid="sidebar-nav">, lets the user reshuffle via HTML5 native
// drag-and-drop, then persists the resulting order to backend +
// localStorage.
//
// Activation : double-click on the sidebar logo (`[data-testid="app-logo-sidebar"]`).
//   • 1st double-click enters edit mode (dotted blue outline, draggable handles,
//     banner).
//   • 2nd double-click commits the current order to backend + localStorage.
//   • Escape key cancels and reverts to the saved order.
//
// Recognised testids inside the sidebar :
//   • `sidebar-group-<slug>` — collapsible groups (e.g. "sidebar-group-daily").
//   • `nav-section-<slug>`   — static section labels (e.g. "nav-section-spending").
//   • `nav-<slug>`           — leaf NavItems (e.g. "nav-dashboard").
//
// Locked items (never draggable) :
//   • The Dashboard NavItem (`nav-dashboard`).
//
// Persistence shape (stored on `users.{id}.sidebar_layout`) :
//   {
//     version: 1,
//     groups: [
//       { id: "sidebar-group-daily", items: ["nav-dashboard", "nav-timesheet"] },
//       { id: "nav-section-spending" },
//       { id: "sidebar-group-spending-ndf", items: [...] },
//       ...
//     ],
//     updated_at: <iso>
//   }
import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "./api";

const STORAGE_KEY = "baim_sidebar_layout_v1";
const LOCKED_ITEM_TESTIDS = new Set(["nav-dashboard"]);

// Returns true if the testid identifies a top-level draggable block under
// <nav data-testid="sidebar-nav">. Excludes nested testids that happen to
// start with the same prefix (e.g. sidebar-group-toggle-…).
function isTopLevelGroupTid(tid) {
  if (!tid) return false;
  if (tid.startsWith("nav-section-")) return true;
  if (tid.startsWith("sidebar-group-")) {
    // Exclude inner descendants such as sidebar-group-toggle-* / -divider-* / -badge-*.
    const tail = tid.slice("sidebar-group-".length);
    return !tail.startsWith("toggle-") && !tail.startsWith("divider-") && !tail.startsWith("badge-");
  }
  return false;
}


/** Read the current DOM order under <nav data-testid="sidebar-nav"> and
 * return a serialisable layout object. */
function readDomLayout(navEl) {
  const groups = [];
  for (const child of Array.from(navEl.children)) {
    const tid = child.getAttribute("data-testid") || "";
    if (!isTopLevelGroupTid(tid)) continue;
    if (tid.startsWith("nav-section-")) {
      groups.push({ id: tid });
      continue;
    }
    // Collect NavItem testids in DOM order. Filter out:
    //  - nav-section-* (could accidentally land here — defensive)
    //  - *-badge sub-elements (testid `nav-XXX-badge` inside the <Link>)
    //  - anything that isn't the top-level <a> wrapper
    const items = [];
    child.querySelectorAll('[data-testid^="nav-"]').forEach((n) => {
      const ntid = n.getAttribute("data-testid");
      if (!ntid) return;
      if (ntid.startsWith("nav-section-")) return;
      if (ntid.endsWith("-badge")) return;
      if (n.tagName !== "A") return;
      items.push(ntid);
    });
    groups.push({ id: tid, items });
  }
  return { version: 1, groups };
}


/** Reorder <nav> children + each group's NavItems to match the saved layout.
 * Items/groups missing from the layout retain their JSX order at the end.
 *
 * Iter-207bg — supports CROSS-GROUP moves: when a saved layout places an
 * item in a different group than where JSX rendered it, we move the DOM
 * node into the destination group's items container. We look up nodes
 * globally across <nav> instead of within each group.
 */
function applyLayoutToDom(navEl, layout) {
  if (!navEl || !layout || !Array.isArray(layout.groups)) return;
  // Collect ALL direct group/section children of <nav>.
  const childMap = new Map();
  for (const child of Array.from(navEl.children)) {
    const tid = child.getAttribute("data-testid") || "";
    if (isTopLevelGroupTid(tid)) childMap.set(tid, child);
  }
  // Collect EVERY NavItem currently rendered anywhere in <nav>, regardless
  // of which group its JSX put it in. This is what enables cross-group
  // persistence: the saved layout can declare "nav-timesheet belongs in
  // Operations" even if AppLayout renders it under Daily.
  //
  // Iter-207bi — Exclude badge <span> elements (testid `nav-*-badge`) and
  // sub-elements that just happen to share the `nav-` prefix. Otherwise
  // the reorder logic appends them as siblings of the NavItems, which
  // makes the orange notification badges appear as full-width bars under
  // every relevant menu item.
  const allItems = new Map();
  navEl.querySelectorAll('[data-testid^="nav-"]').forEach((n) => {
    const tid = n.getAttribute("data-testid");
    if (!tid) return;
    if (tid.startsWith("nav-section-")) return;
    if (tid.endsWith("-badge")) return;
    // We only ever want the top-level <a> NavItem wrapper, not any child.
    if (n.tagName !== "A") return;
    allItems.set(tid, n);
  });

  // 1) Reorder top-level entries (sections + group containers).
  for (const g of layout.groups) {
    const node = childMap.get(g.id);
    if (node) navEl.appendChild(node);
  }
  // 2) Inside each group, reorder its items in the saved order — moving
  //    DOM nodes between groups if necessary.
  for (const g of layout.groups) {
    const node = childMap.get(g.id);
    if (!node || !Array.isArray(g.items) || g.items.length === 0) continue;
    // Find the items container inside this group. SidebarGroup renders
    // <div data-testid="sidebar-group-X"><button>…</button><div className="flex flex-col gap-1">…items…</div></div>.
    let container = node.querySelector(':scope > div.flex.flex-col') || node;
    // Fallback: pick the parent of the first existing inner NavItem.
    if (container === node) {
      const inner = node.querySelector('[data-testid^="nav-"]:not([data-testid^="nav-section-"])');
      if (inner && inner.parentElement) container = inner.parentElement;
    }
    for (const itemId of g.items) {
      const n = allItems.get(itemId);
      if (n) container.appendChild(n); // appendChild moves the node if it's elsewhere.
    }
  }
}


export function useSidebarDragMode() {
  const [editMode, setEditMode] = useState(false);
  const [savedLayout, setSavedLayout] = useState(null);
  const navRef = useRef(null);
  // Snapshot of the layout at edit-mode entry, used to revert on Escape.
  const revertSnapshotRef = useRef(null);

  // Load saved layout on mount.
  useEffect(() => {
    (async () => {
      // Iter-207bl — Emergency escape hatch: visiting any page with
      // `?reset-sidebar=1` (or `&reset-sidebar=1`) wipes the saved layout
      // both client- and server-side. Use this when the saved layout is
      // corrupt and the UI is stuck/frozen so the user can't reach the
      // edit-mode reset button.
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get("reset-sidebar") === "1") {
          try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
          try { await api.put("/me/sidebar-layout", { layout: null }); } catch { /* noop */ }
          setSavedLayout(null);
          // Strip the query param so a refresh doesn't keep resetting.
          url.searchParams.delete("reset-sidebar");
          window.history.replaceState({}, "", url.toString());
          return;
        }
      } catch { /* noop */ }
      try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) setSavedLayout(JSON.parse(cached));
      } catch { /* noop */ }
      try {
        const r = await api.get("/me/sidebar-layout");
        if (r.data?.layout) {
          setSavedLayout(r.data.layout);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(r.data.layout));
        } else if (r.data && r.data.layout === null) {
          // Backend says "no custom layout" — clear any stale cache.
          setSavedLayout(null);
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch { /* noop */ }
    })();
  }, []);

  // Apply the saved layout to the DOM on mount and whenever it changes.
  //
  // Iter-207bl — REMOVED the MutationObserver entirely. The previous
  // versions (subtree: true → infinite loop; subtree: false → still looped
  // because appendChild fires mutation records even when "moving to the
  // same position", and the `applying` guard reset synchronously). React
  // does NOT spontaneously reorder NavItem children of <nav>, so a one-
  // time application per layout change is enough. Any rare case where
  // React swaps DOM nodes (e.g., role change, sandbox swap) is recovered
  // on the next page navigation, which is acceptable.
  useEffect(() => {
    const nav = document.querySelector('nav[data-testid="sidebar-nav"]');
    if (!nav) return undefined;
    navRef.current = nav;
    if (!savedLayout) return undefined;
    // Defer one frame so React finishes mounting before we mutate.
    const id = requestAnimationFrame(() => {
      try { applyLayoutToDom(nav, savedLayout); }
      catch (e) { console.warn("[sidebar] applyLayoutToDom failed:", e); }
    });
    return () => cancelAnimationFrame(id);
  }, [savedLayout, editMode]);

  // HTML5 native drag-and-drop wiring in edit mode.
  useEffect(() => {
    if (!editMode) return undefined;
    const nav = navRef.current || document.querySelector('nav[data-testid="sidebar-nav"]');
    if (!nav) return undefined;
    let dragged = null;
    // Iter-207bd — visual drop indicator. Single bar repositioned on dragover.
    // It's a child of <nav> (which is the scrollable container) so it scrolls
    // with the menu and we can position it absolutely with respect to the nav.
    if (getComputedStyle(nav).position === "static") nav.style.position = "relative";
    const indicator = document.createElement("div");
    indicator.setAttribute("data-testid", "sidebar-drop-indicator");
    indicator.style.cssText = [
      "position:absolute",
      "left:4px",
      "right:4px",
      "height:4px",
      "background:#2563eb",          // blue-600
      "border-radius:9999px",
      "box-shadow:0 0 0 2px rgba(37,99,235,0.30), 0 2px 6px rgba(37,99,235,0.45)",
      "pointer-events:none",
      "opacity:0",
      "transition:top 80ms ease-out, opacity 120ms ease-out",
      "z-index:50",
    ].join(";");
    nav.appendChild(indicator);
    let lastTarget = null;
    let lastBefore = null;

    const hideIndicator = () => {
      indicator.style.opacity = "0";
      lastTarget = null;
      lastBefore = null;
    };
    const showIndicatorAt = (targetEl, before) => {
      // Compute Y relative to <nav>'s padding box (including its scroll).
      const rect = targetEl.getBoundingClientRect();
      const navRect = nav.getBoundingClientRect();
      const y = (before ? rect.top : rect.bottom) - navRect.top + nav.scrollTop - 1.5;
      indicator.style.top = `${Math.max(0, y)}px`;
      indicator.style.opacity = "1";
      lastTarget = targetEl;
      lastBefore = before;
    };

    const isItem = (el) => {
      const t = el?.getAttribute?.("data-testid") || "";
      return t.startsWith("nav-") && !t.startsWith("nav-section-");
    };
    const isGroup = (el) => isTopLevelGroupTid(el?.getAttribute?.("data-testid") || "");

    const onDragStart = (e) => {
      const el = e.target.closest('[data-testid]');
      if (!el || !nav.contains(el)) return;
      const tid = el.getAttribute("data-testid") || "";
      if (LOCKED_ITEM_TESTIDS.has(tid)) { e.preventDefault(); return; }
      if (!isItem(el) && !isGroup(el)) return;
      dragged = el;
      e.dataTransfer.effectAllowed = "move";
      try { e.dataTransfer.setData("text/plain", tid); } catch { /* noop */ }
      el.classList.add("opacity-50");
    };
    const onDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (!dragged) return;
      const target = e.target.closest('[data-testid]');
      if (!target || target === dragged || !nav.contains(target)) { hideIndicator(); return; }
      const draggedItem = isItem(dragged);
      const draggedGroup = isGroup(dragged);
      const targetItem = isItem(target);
      const targetGroup = isGroup(target);
      // Only show the indicator on valid drop targets that mirror the drop logic.
      const validPair =
        (draggedItem && targetItem)
        || (draggedItem && targetGroup)
        || (draggedGroup && targetGroup);
      if (!validPair) { hideIndicator(); return; }
      const rect = target.getBoundingClientRect();
      const before = (e.clientY - rect.top) < rect.height / 2;
      // For a NavItem dropped onto a group, we always append at the end of
      // the group, so the indicator should sit at the group's bottom.
      const visualBefore = (draggedItem && targetGroup) ? false : before;
      if (target !== lastTarget || visualBefore !== lastBefore) {
        showIndicatorAt(target, visualBefore);
      }
    };
    const onDrop = (e) => {
      e.preventDefault();
      hideIndicator();
      if (!dragged) return;
      const target = e.target.closest('[data-testid]');
      dragged.classList.remove("opacity-50");
      if (!target || target === dragged || !nav.contains(target)) { dragged = null; return; }
      const draggedItem = isItem(dragged);
      const draggedGroup = isGroup(dragged);
      const targetItem = isItem(target);
      const targetGroup = isGroup(target);

      if (draggedItem && targetItem) {
        // Insert before or after the target depending on cursor position.
        const rect = target.getBoundingClientRect();
        const before = (e.clientY - rect.top) < rect.height / 2;
        if (before) {
          target.parentNode.insertBefore(dragged, target);
        } else {
          target.parentNode.insertBefore(dragged, target.nextSibling);
        }
      } else if (draggedItem && targetGroup) {
        // Drop NavItem onto a group → append to its item container.
        const firstItem = target.querySelector('[data-testid^="nav-"]:not([data-testid^="nav-section-"])');
        if (firstItem) firstItem.parentNode.appendChild(dragged);
      } else if (draggedGroup && targetGroup) {
        // Reorder top-level: before or after the target group.
        const rect = target.getBoundingClientRect();
        const before = (e.clientY - rect.top) < rect.height / 2;
        if (before) {
          target.parentNode.insertBefore(dragged, target);
        } else {
          target.parentNode.insertBefore(dragged, target.nextSibling);
        }
      }
      dragged = null;
    };
    const onDragEnd = () => {
      if (dragged) dragged.classList.remove("opacity-50");
      hideIndicator();
      dragged = null;
    };
    const onDragLeave = (e) => {
      // Hide the indicator when the cursor leaves the <nav> entirely.
      if (e.target === nav && !nav.contains(e.relatedTarget)) hideIndicator();
    };

    // Mark draggable elements + visual indicators.
    const markableSel = '[data-testid^="nav-"], [data-testid^="sidebar-group-"]';
    const markable = nav.querySelectorAll(markableSel);
    const marked = [];
    markable.forEach((el) => {
      const tid = el.getAttribute("data-testid") || "";
      if (LOCKED_ITEM_TESTIDS.has(tid)) return;
      // Only mark the elements we actually consider draggable.
      if (!isItem(el) && !isGroup(el)) return;
      el.setAttribute("draggable", "true");
      el.classList.add("ring-1", "ring-blue-300", "cursor-move", "rounded-sm");
      marked.push(el);
    });
    nav.addEventListener("dragstart", onDragStart);
    nav.addEventListener("dragover", onDragOver);
    nav.addEventListener("drop", onDrop);
    nav.addEventListener("dragend", onDragEnd);
    nav.addEventListener("dragleave", onDragLeave);

    return () => {
      marked.forEach((el) => {
        el.removeAttribute("draggable");
        el.classList.remove("ring-1", "ring-blue-300", "cursor-move", "rounded-sm", "opacity-50");
      });
      nav.removeEventListener("dragstart", onDragStart);
      nav.removeEventListener("dragover", onDragOver);
      nav.removeEventListener("drop", onDrop);
      nav.removeEventListener("dragend", onDragEnd);
      nav.removeEventListener("dragleave", onDragLeave);
      if (indicator.parentNode) indicator.parentNode.removeChild(indicator);
    };
  }, [editMode]);

  // Escape to cancel edit mode without saving.
  useEffect(() => {
    if (!editMode) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        const snap = revertSnapshotRef.current;
        if (snap && navRef.current) applyLayoutToDom(navRef.current, snap);
        setEditMode(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editMode]);

  const enterEditMode = useCallback(() => {
    const nav = navRef.current || document.querySelector('nav[data-testid="sidebar-nav"]');
    if (!nav) return;
    navRef.current = nav;
    revertSnapshotRef.current = readDomLayout(nav);
    setEditMode(true);
  }, []);

  const saveAndExit = useCallback(async () => {
    const nav = navRef.current || document.querySelector('nav[data-testid="sidebar-nav"]');
    if (!nav) return;
    const layout = readDomLayout(nav);
    setSavedLayout(layout);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(layout)); } catch { /* noop */ }
    setEditMode(false);
    try {
      await api.put("/me/sidebar-layout", { layout });
    } catch { /* fail silent — localStorage already updated */ }
  }, []);

  const resetToDefault = useCallback(async () => {
    if (!window.confirm("Réinitialiser la sidebar au layout standard ?")) return;
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    setSavedLayout(null);
    setEditMode(false);
    try { await api.put("/me/sidebar-layout", { layout: null }); } catch { /* noop */ }
    window.location.reload();
  }, []);

  const onLogoDoubleClick = useCallback(() => {
    if (editMode) saveAndExit();
    else enterEditMode();
  }, [editMode, enterEditMode, saveAndExit]);

  return { editMode, onLogoDoubleClick, saveAndExit, resetToDefault };
}
